import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase D — Self profile.
 *
 * Client users may edit ONLY their own first_name, last_name, phone.
 * Email, role, report_scope, permissions, company, team, and status are
 * NTN-owned and rendered read-only with an info hint.
 */
export default function MyProfilePanel() {
  const { user, refresh, reportScope, roleName, companyLabel } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      phone: user?.phone || "",
    });
  }, [user?.id, user?.first_name, user?.last_name, user?.phone]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (form.first_name !== (user?.first_name || "")) payload.first_name = form.first_name;
      if (form.last_name !== (user?.last_name || "")) payload.last_name = form.last_name;
      if (form.phone !== (user?.phone || "")) payload.phone = form.phone;
      if (!Object.keys(payload).length) {
        toast.info("No changes");
        return;
      }
      await apiClient.patch("/account/profile", payload);
      toast.success("Profile updated");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="my-profile-panel">
      <Card className="p-8 rounded-sm border border-[#0A192F]/10">
        <h2 className="ntn-h2 mb-1">My Profile</h2>
        <p className="ntn-secondary mb-6">
          Update the name and phone number that appear on your reports and messages.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568]">First name</Label>
            <Input
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="mt-2 h-11 rounded-sm"
              data-testid="profile-first-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568]">Last name</Label>
            <Input
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="mt-2 h-11 rounded-sm"
              data-testid="profile-last-name"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568]">Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 555 123 4567"
              className="mt-2 h-11 rounded-sm"
              data-testid="profile-phone"
            />
          </div>
        </div>

        <Button
          onClick={save}
          disabled={busy}
          className="mt-6 bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm"
          data-testid="profile-save"
        >
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save changes"}
        </Button>
      </Card>

      <Card className="p-8 rounded-sm border border-[#0A192F]/10 bg-slate-50/50">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="h-4 w-4 text-slate-400" />
          <h3 className="ntn-h3">Managed by NTN</h3>
        </div>
        <p className="text-sm text-[#4A5568] mb-5">
          These fields are administered by NTN. To request a change, contact your
          NTN account manager.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReadOnly label="Email"        value={user?.email} testid="profile-ro-email" />
          <ReadOnly label="Role"         value={roleName || "—"} testid="profile-ro-role" />
          <ReadOnly label="Report scope" value={(reportScope || "self").toUpperCase()} testid="profile-ro-scope" />
          <ReadOnly label="Company"      value={companyLabel || user?.company} testid="profile-ro-company" />
          <ReadOnly label="Team"         value={user?.primary_team_id || "—"} testid="profile-ro-team" />
          <ReadOnly label="Status"       value={user?.status || (user?.disabled_at ? "inactive" : "active")} testid="profile-ro-status" />
        </div>
      </Card>
    </div>
  );
}

function ReadOnly({ label, value, testid }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">{label}</div>
      <div className="text-sm text-[#0A192F] mt-1 truncate" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}
